import React, { useCallback, useEffect, useMemo, useState } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import {
  useNodePerformanceContext,
  useReactNodeView,
  useRequestContext,
  useVideoDownloadContext,
  useVideoOverlayContext,
} from '@lyearn/lygo-core';
import { isShakaSupported, isVideoProcessing } from '@lyearn/shared/components/Players';

import useDrop from '../../../hooks/useDrop';
import useUpload, { FileType } from '../../../hooks/useUpload';
import { isInTable, isInTabs, isInToggleList } from '../../../utils';
import {
  getCloudflareRequestURL,
  getCloudflareStreamingIdFromUploadResponse,
  resumeVideoProcessing,
} from './utils';
import VideoComponent from './Video';

const VideoNodeView: React.FC = () => {
  const { node, view, updateAttrs, getPos } = useReactNodeView();
  const { setNodePerformance, getNodePerformance } = useNodePerformanceContext();
  const { enabledConvertibleImport } = useRequestContext();
  const { getRequest } = useRequestContext();
  const [processingProgress, setProcessingProgress] = useState<number | undefined>();
  const [showOverlay, { on: onShowOverlay, off: onCloseOverlay }] = useToggle();

  const videoOverlayContext = useVideoOverlayContext();

  const videoDownloadContext = useVideoDownloadContext();

  const updateEditorState = useCallback(
    (file: FileType) => {
      if (enabledConvertibleImport) {
        const streamId = getCloudflareStreamingIdFromUploadResponse(file.url ?? '');
        updateAttrs({
          processing: true,
          name: file.name,
          streamId,
        });
        getRequest?.(getCloudflareRequestURL(streamId)).then((res: any) => {
          updateAttrs({
            src: res.data.playback.dash,
            processing: true,
            name: file.name,
            thumbnail: res.data.thumbnail,
            streamId,
          });
        });
      } else {
        updateAttrs({
          src: file.url,
          name: file.name,
        });
      }
    },
    [enabledConvertibleImport, getRequest, updateAttrs],
  );

  const onLoad = useCallback(
    (duration: number) => {
      if (view?.editable && node?.attrs.duration !== Math.trunc(duration)) {
        updateAttrs({ duration: duration });
      }
    },
    [node, updateAttrs, view],
  );

  const setPerformance = useCallback(
    (progress: number) => {
      node?.attrs.id && setNodePerformance?.({ id: node?.attrs.id, blockType: 'video', progress });
    },
    [node?.attrs.id, setNodePerformance],
  );

  const onProgress = useCallback(
    (attrs: { time: number }) => {
      setPerformance(Math.ceil((attrs.time / node?.attrs.duration) * 100));
    },
    [node?.attrs.duration, setPerformance],
  );

  useEffect(() => {
    // set performance for empty block where video is still to be attach
    if (!node?.attrs.src && !view?.editable) {
      setPerformance?.(100);
    }
  }, [node?.attrs.src, setPerformance, view?.editable]);

  useEffect(() => {
    if (
      node?.attrs.processing &&
      node?.attrs.src &&
      node?.attrs.streamId &&
      isShakaSupported(node?.attrs.src)
    ) {
      isVideoProcessing(
        getCloudflareRequestURL(node.attrs.streamId),
        setProcessingProgress,
        getRequest,
      ).then(() => {
        updateAttrs({
          processing: null,
          streamId: null,
        });
      });
    } else if (node?.attrs.processing && node?.attrs.streamId && !node?.attrs.src) {
      const streamId = node.attrs.streamId;
      resumeVideoProcessing({ streamId, updateAttrs, getRequest, getCloudflareRequestURL });
    }
  }, [getRequest, node?.attrs.processing, node?.attrs.src, node?.attrs.streamId, updateAttrs]);

  const { maxBlockProgress, initalBlockProgress } = useMemo(() => {
    return {
      maxBlockProgress:
        getNodePerformance?.(node?.attrs.id)?.progress || (view?.editable ? 100 : 0),
      initalBlockProgress: getNodePerformance?.(node?.attrs.id)?.progress,
    };
  }, [getNodePerformance, node?.attrs.id, view?.editable]);

  const { currentFile, uppy } = useUpload(updateEditorState, node?.attrs.id);
  useDrop(node, updateAttrs, uppy);

  const overlay = useMemo(
    () => ({
      onShowOverlay,
      hideOverlay: onCloseOverlay,
      renderOverlay:
        showOverlay && videoOverlayContext?.renderOverlay
          ? videoOverlayContext?.renderOverlay
          : null,
    }),
    [onCloseOverlay, onShowOverlay, showOverlay, videoOverlayContext?.renderOverlay],
  );

  const showFullWidth =
    typeof getPos === 'function' &&
    (isInTable(view, getPos()) || isInTabs(view, getPos()) || isInToggleList(view, getPos()));

  return (
    <VideoComponent
      {...node?.attrs}
      enableDownloadVideo={videoDownloadContext.enableDownloadVideo}
      initialProgress={initalBlockProgress}
      isProcessing={!!node?.attrs.processing}
      isUploading={!!currentFile}
      maxPlayerProgress={maxBlockProgress}
      // If progress is an object then it means we are fetching the required URL from
      // the cloudflare and the upload is completed. If there is no currentFile, progress
      // props doesn't matter.
      overlay={overlay}
      processingProgress={processingProgress}
      progress={Number.isInteger(currentFile?.fileProgress) ? currentFile!.fileProgress! : 100}
      progressFileName={currentFile?.name || node?.attrs.name || node?.attrs.src}
      readOnly={!view?.editable}
      showFullWidth={showFullWidth}
      onDownload={videoDownloadContext.onDownload}
      onLoad={onLoad}
      onProgress={onProgress}
    />
  );
};

export default VideoNodeView;
